<script>
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default {
  components: {
    // eslint-disable-next-line vue/no-reserved-component-names
    Line
  },
  props: {
    /**
     * Example of data format:
     * {
     *   labels: ['String1', 'String2', 'String3', 'String4'],
     *   datasets: [
     *     {
     *       backgroundColor: ['#41B883', '#E46651', '#00D8FF', '#DD1B16'],
     *       data: [40, 20, 80, 10]
     *     }
     *   ]
     * }
     */
    data: {
      type: Object,
      required: true
    },
    options: {
      type: Object,
      default: () => {}
    },
    yPrefix: {
      type: String,
      default: ""
    },
    legendAlign: {
      type: String,
      default: "start"
    },
    fiscalYearStartLabel: {
      type: String,
      default: ""
    }
  },
  setup() {
    return { };
  },
  computed: {
    // Chart JS options
    // https://www.chartjs.org/docs/
    defaultOptions() {
      const self = this; // Capture the component context for callback

      return  {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            align: this.legendAlign,
            display: true,
            labels: {
              usePointStyle: true,
              pointStyleWidth: 7,
              boxHeight: 5,
              filter: item => item.text !== undefined
            }
          },
          tooltip: {
            enabled: true,
            intersect: false,
            mode: 'nearest',
            callbacks: {
              title: function(tooltipItems) {
                const label = tooltipItems[0]?.label;
                // If it's a yearly tooltip (e.g. "2024")
                if (label && /^\d{4}$/.test(label)) {
                  const now = new Date();
                  const labels = tooltipItems[0]?.chart?.data?.labels || [];
                  const idx = tooltipItems[0]?.dataIndex;
                  const isProjected = tooltipItems[0]?.dataset?.label?.toLowerCase().includes('projected');
                  if (isProjected) return `${label} (Fiscal Year - Projected)`;
                  if (idx === labels.length - 1) return `${label} (Fiscal Year - through ${now.toLocaleString('default', { month: 'long' })})`;
                  return `${label} (Fiscal Year)`;
                }
                return label;
              },
              label: function(context) {
                let label = context.dataset.label || '';
                let value = context.parsed.y;
                if (self.yPrefix) {
                  return label + ': ' + self.yPrefix + value.toLocaleString();
                }
                return label + ': ' + value.toLocaleString();
              }
            }
          },
          interaction: {
            intersect: false
          }
        },
        scales: {
          x: {
            grid: { display: false }, // No vertical lines on grid
          },
          y: {
            grid: { display: false }, // No horizontal lines on grid, white bg
            ticks: {
              display: true,
              precision: self.yPrefix ? undefined : 0,
              callback: function(value) { // Prefixes the labels with specified string ($)
                if(self.yPrefix){
                  return self.yPrefix.toString() + value;
                }else{
                  return value;
                }
              }},
            border: { display: false },
            beginAtZero: true,
          },
        },
      };
    },
    plugins() {
      return [
        {
          id: "legendMargin",
          beforeInit: function (chart) {
            const fitValue = chart.legend.fit;
            chart.legend.fit = function fit() {
              fitValue.bind(chart.legend)();
              return (this.height += 20);
            };
          }
        },
        {
          // Shows the horizontal line on hover for a data point
          afterDraw: chart => {
            if (chart.tooltip?._active?.length) {
              let x = chart.tooltip._active[0].element.x;
              let yAxis = chart.scales.y;
              let ctx = chart.ctx;
              ctx.save();
              ctx.beginPath();
              ctx.setLineDash([5, 5]);
              ctx.moveTo(x, yAxis.top);
              ctx.lineTo(x, yAxis.bottom);
              ctx.lineWidth = 1;
              ctx.strokeStyle = 'rgba(0, 0, 255, 0.4)';
              ctx.stroke();
              ctx.restore();
            }
          }
        },
        {
          // Draws a vertical dashed line at the fiscal year start month
          id: 'fiscalYearLine',
          afterDraw: chart => {
            const fiscalLabel = this.fiscalYearStartLabel;
            if (!fiscalLabel) return;
            const labels = chart.scales.x?.ticks?.map(t => t.label) || [];
            const index = labels.findLastIndex(l => l && l.startsWith(fiscalLabel));
            if (index < 0) return;
            const xScale = chart.scales.x;
            const x = xScale.getPixelForValue(index);
            const yAxis = chart.scales.y;
            const ctx = chart.ctx;
            ctx.save();
            ctx.beginPath();
            ctx.setLineDash([4, 4]);
            ctx.moveTo(x, yAxis.top);
            ctx.lineTo(x, yAxis.bottom);
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = 'rgba(255, 140, 0, 0.6)';
            ctx.stroke();
            ctx.fillStyle = 'rgba(255, 140, 0, 0.8)';
            ctx.font = '10px sans-serif';
            ctx.textAlign = 'center';
            const textWidth = ctx.measureText('Fiscal Year Start').width;
            const minX = xScale.left + (textWidth / 2);
            const textX = Math.max(x, minX);
            ctx.fillText('Fiscal Year Start', textX, yAxis.top - 5);
            ctx.restore();
          }
        }

      ];
    }

  },
};
</script>

<template>
  <Line
    :data="data"
    :options="(options) ? options : defaultOptions"
    :plugins="plugins"
    class="h-full max-w-full"
  />
</template>

<style lang="scss"></style>
